// Home's words (lane B10 S16): the report families a Home widget links to, the Search Readiness prerequisites and the
// Req health drivers by their keys, and the accessible names of the rings. Every entry quotes his glossary or the
// vendored design-system excerpts (the D0 label suites discover this module, a lane folder of src/views, and hold it to
// the same rules as src/mc). Words the design system already carries (the KPI names, Req health, Search Readiness,
// Drivers, Missing prerequisites, the band words, the pipeline columns, Requisition timeline, the nav words, the status
// chains) are imported where they are drawn, never redefined here. A report's "not yet available" figure is drawn as
// "—": the word is DCR-B10-1's and is not in the vendored sources yet, and D0's suite holds `pending` to the words it
// lists (a deviation from PLAN D17, recorded in reports/S16.md).

// Label modules only (never the "@/mc" barrel): the word suites load this file without the components.
import {
  HEALTH_BAND_LABELS, RECRUITER_ASSIGNMENT_STATUS, REQUISITION_STATUS, SLA_LABELS, SLA_TIER_LABELS, defineLabels, template,
} from "@/mc/labels";
import { METRIC_LABELS } from "@/mc/g3/labels";
import { MILESTONE_LABELS } from "@/mc/g5/labels";

const RING_WORDS = "*Req health* · *Search Readiness* · n/N gates";
const CLOCK_WORDS = "*Calendar Time* · *Active Time* · *Days on Hold* · *Time to Acceptance* · *Time to Fill* · \"SLA uses Active Time\" · 60 / 75 / 90";
const WIDGETS = "*Own open reqs* · *Assigned interviewer work elsewhere* · *Assigned reqs/interviews/packets/scorecards/debriefs* · scope words";
const READINESS = "| Search Readiness | \"Search Readiness percent with missing prerequisites; posting is not blocked by pending Compensation.\"";
const REQ_CHAIN = "Chain \"Request Approved -> Req Creation Pending -> Created -> Posted.\"";
const POSTING = "| Posting strategy | \"Preserve proposed vs current posting strategy and reason for change.\"";
const COMP_REVIEW = "| Compensation review | Chain \"Compensation: Pending -> In Review -> Finalized.\" \"Compensation review workstream\".";
const PHASE = "| Phase | a filter (\"status, phase, health\");";

/** His report names (G §14) and the Executive dashboard (G §2), keyed by report family. */
export const REPORT_FAMILY_LABELS = /* @__PURE__ */ defineLabels({
  portfolio: { text: "Portfolio" },
  executive: { text: "Executive dashboard" },
  pipeline: { text: "Pipeline" },
  offer: { text: "Offer" },
  candidate_experience: { text: "Candidate experience" },
  hm_responsiveness: { text: "HM responsiveness" },
  recruiter_operations: { text: "Recruiter operations" },
  coordinator_operations: { text: "Coordinator operations" },
});

/** The widget titles and sentences that are Home's own. */
export const HOME_LABELS = /* @__PURE__ */ defineLabels({
  dispositionWorklist: { text: "Disposition worklist" 

},
  assignedList: { text: "Assigned reqs/interviews/packets/scorecards/debriefs" },
  postingNotBlocked: { text: "Posting is not blocked by pending Compensation." },
  requestApproved: { text: "Request Approved" },
  postingStrategy: { text: "Posting strategy" },
  compensationReview: { text: "Compensation review" },
  phase: { text: "Phase" },
});

/** The rings' accessible names: each carries its value (DS row 37 "accessible name required"). */
export const HOME_TEMPLATES = {
  reqHealthMean: /* @__PURE__ */ template({ text: "Req health {n}" }),
  searchReadinessPct: /* @__PURE__ */ template({ text: "Search Readiness {pct}" }),
  withinTargets: /* @__PURE__ */ template({ text: "{pct} · 60 / 75 / 90" }),
} as const;

/** Search Readiness prerequisites by key (B1 `spine/readiness.py`; B4 adds `interview_plan_ready`, drawn by its count). */
export const PREREQUISITE_WORDS: Readonly<Record<string, string>> = {
  request_approved: HOME_LABELS.requestApproved,
  requisition_created: REQUISITION_STATUS.created,
  recruiter_assigned: RECRUITER_ASSIGNMENT_STATUS.assigned,
  posting_strategy_set: HOME_LABELS.postingStrategy,
  intake_finalized: MILESTONE_LABELS.intake,
  compensation_review: HOME_LABELS.compensationReview,
};

/** Req health drivers by key (B1 `spine/vocab.HEALTH_DRIVER_KEYS`, in display order; D-24: *Days open*, *HM
 * responsiveness*, *Overdue review* lead). `phase_state` is no longer computed; a snapshot stored before D-24 still
 * carries it, and its word stays. */
export const DRIVER_WORDS: Readonly<Record<string, string>> = {
  days_open: METRIC_LABELS.daysOpen,
  hm_responsiveness: METRIC_LABELS.hmResponsiveness,
  overdue_actions: METRIC_LABELS.overdueReview,
  sla_breaches: SLA_TIER_LABELS.breach,
  time_to_acceptance: METRIC_LABELS.timeToAcceptance,
  hold_days: SLA_LABELS.daysOnHold,
  phase_state: HOME_LABELS.phase,
};

/** The five KPI cards of DC-51, in his order. */
export const KPI_WORDS: Readonly<Record<string, string>> = {
  open_requisitions: METRIC_LABELS.openRequisitions,
  active_candidates: METRIC_LABELS.activeCandidates,
  time_to_acceptance: METRIC_LABELS.timeToAcceptance,
  offers_accepted: METRIC_LABELS.offersAccepted,
  req_health: METRIC_LABELS.reqHealth,
};

/** A requisition's status (models/enums.REQUISITION_STATUS) as his status word (G §5). CR-034: `accepted` is Offer
 * Accepted, a milestone, not Filled; `filled` is the HRIS hire. */
export const REQUISITION_STATUS_WORDS: Readonly<Record<string, string>> = {
  draft: REQUISITION_STATUS.created,
  open: REQUISITION_STATUS.posted,
  partially_filled: REQUISITION_STATUS.posted,
  on_hold: REQUISITION_STATUS.onHold,
  accepted: REQUISITION_STATUS.offerAccepted,
  filled: REQUISITION_STATUS.filled,
  closed_filled: REQUISITION_STATUS.filled,
  closed_cancelled: REQUISITION_STATUS.cancelled,
  archived: REQUISITION_STATUS.archived,
};

/** The three bands, Red first (the order Req health lists them in). */
export const BAND_ORDER = ["red", "yellow", "green"] as const;
export type Band = (typeof BAND_ORDER)[number];
export const BAND_WORDS: Readonly<Record<Band, string>> = {
  red: HEALTH_BAND_LABELS.red,
  yellow: HEALTH_BAND_LABELS.yellow,
  green: HEALTH_BAND_LABELS.green,
};
